import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { authActions } from '../../stores/authStore';
import { loadCompanyData } from '../../utils/loadData';
import { PasswordField } from '../../components/PasswordField';
import type { CompanyMembership } from 'shared/types/auth';

export const Route = createFileRoute('/login/')({
  component: LoginPage,
});

type LoginStage =
  | { kind: 'credentials' }
  | {
      kind: 'otp';
      challengeToken: string;
      method: 'sms' | 'totp';
      maskedPhone: string | null;
      mustChangePassword: boolean;
    }
  | { kind: 'select-tenant'; selectionToken: string; companies: CompanyMembership[] }
  // Certain SSO: between submitting the credentials and showing the OTP step
  // we poll login-status until BeCertain confirms it sent the SMS. `email` is
  // remembered so the verify call has it without the user retyping.
  | { kind: 'certain-pending'; email: string }
  | { kind: 'certain-otp'; email: string };

function LoginPage() {
  const navigate = useNavigate();

  // Auth config — drives whether we use the legacy one-step flow or the
  // new two-step (password + SMS OTP) flow, plus whether the Certain SSO
  // button appears. While this is loading we show a tiny placeholder so
  // the form layout doesn't flash.
  const [config, setConfig] = useState<{
    enhancedAuthEnabled: boolean;
    becertainEnabled: boolean;
    demoEnabled?: boolean;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    authActions.getAuthConfig()
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch(() => { if (!cancelled) setConfig({ enhancedAuthEnabled: false, becertainEnabled: false }); });
    return () => { cancelled = true; };
  }, []);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [stage, setStage] = useState<LoginStage>({ kind: 'credentials' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Show a notice if the user landed here because the idle-logout guard signed
  // them out (flag set in IdleLogoutGuard before redirect).
  const [idleNotice, setIdleNotice] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem('arm.idleLogout') === '1') {
        setIdleNotice(true);
        sessionStorage.removeItem('arm.idleLogout');
      }
    } catch {
      // sessionStorage unavailable — no banner.
    }
  }, []);

  function reset() {
    setStage({ kind: 'credentials' });
    setOtp('');
    setError('');
    setLoading(false);
  }

  // ─── Demo sign-in — password-less, into the shared North Wind tenant ────
  async function handleDemoSignin(variant: 'admin' | 'member') {
    setError('');
    setLoading(true);
    try {
      await authActions.demoSignin(variant);
      loadCompanyData();
      navigate({ to: '/' });
    } catch (err) {
      setError((err as Error).message || 'Demo sign-in failed');
    } finally {
      setLoading(false);
    }
  }

  // ─── Legacy flow (flag off) ─────────────────────────────────────────────
  async function handleLegacySubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authActions.login(email, password);
      loadCompanyData();
      navigate({ to: '/' });
    } catch (err) {
      setError((err as Error).message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  // ─── Native-auth flow (flag on) ────────────────────────────────────────
  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await authActions.startLogin(email, password);
      setStage({
        kind: 'otp',
        challengeToken: r.challengeToken,
        method: r.method,
        maskedPhone: r.maskedPhone,
        mustChangePassword: r.mustChangePassword,
      });
    } catch (err) {
      setError((err as Error).message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== 'otp') return;
    setError('');
    setLoading(true);
    try {
      const r = await authActions.verifyLoginOtp(stage.challengeToken, otp);
      if (r.nextStep === 'tokens') {
        loadCompanyData();
        navigate({ to: '/' });
      } else if (r.nextStep === 'select-tenant') {
        setStage({
          kind: 'select-tenant',
          selectionToken: r.selectionToken,
          companies: r.companies,
        });
      } else if (r.nextStep === 'reset-password') {
        navigate({ to: '/reset-password', search: { token: r.resetToken } as never });
      }
    } catch (err) {
      setError((err as Error).message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectTenant(companyId: string) {
    if (stage.kind !== 'select-tenant') return;
    setError('');
    setLoading(true);
    try {
      await authActions.selectLoginTenant(stage.selectionToken, companyId);
      loadCompanyData();
      navigate({ to: '/' });
    } catch (err) {
      setError((err as Error).message || 'Failed to select workspace');
    } finally {
      setLoading(false);
    }
  }

  // ─── Certain SSO ─────────────────────────────────────────────────────
  // Three-step proxy through to BeCertain: kick off, poll until they've
  // shipped the SMS, verify the OTP. Same email + password fields as the
  // native flow — just a different button at the bottom.

  async function handleCertainStart() {
    if (!email || !password) {
      setError('Enter your email and password first.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await authActions.startCertainLogin(email, password);
      setStage({ kind: 'certain-pending', email });
    } catch (err) {
      setError((err as Error).message || 'Sign-in failed');
      setLoading(false);
    }
  }

  // Poll login-status while we're in the certain-pending stage. Stops as
  // soon as BeCertain responds or we leave the stage.
  useEffect(() => {
    if (stage.kind !== 'certain-pending') return;
    const captured = stage.email;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60; // ~30 seconds at 500ms

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await authActions.pollCertainLoginStatus(captured);
        if (cancelled) return;
        if (r.error) {
          setError(r.error);
          setStage({ kind: 'credentials' });
          setLoading(false);
          return;
        }
        if (r.ready) {
          setStage({ kind: 'certain-otp', email: captured });
          setLoading(false);
          return;
        }
        if (attempts >= maxAttempts) {
          setError("Certain didn't respond in time. Please try again.");
          setStage({ kind: 'credentials' });
          setLoading(false);
          return;
        }
        setTimeout(tick, 500);
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message || 'Sign-in failed');
        setStage({ kind: 'credentials' });
        setLoading(false);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [stage]);

  async function handleCertainVerify(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== 'certain-otp') return;
    setError('');
    setLoading(true);
    try {
      const r = await authActions.verifyCertainOtp(stage.email, otp);
      if (r.nextStep === 'tokens') {
        loadCompanyData();
        navigate({ to: '/' });
      } else if (r.nextStep === 'select-tenant') {
        setStage({
          kind: 'select-tenant',
          selectionToken: r.selectionToken,
          companies: r.companies,
        });
      }
    } catch (err) {
      const ex = err as Error & { status?: number };
      // 403 from /becertain/verify is the entitlement gate firing
      // (subscription_required, no-local-account-and-company-exists, etc.).
      // Leaving the user staring at an OTP form they can't satisfy is
      // confusing — bounce them back to the credentials step where the
      // alternative sign-in methods are visible and the rejection sits at
      // the top.
      if (ex.status === 403) {
        setStage({ kind: 'credentials' });
        setOtp('');
      }
      setError(ex.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  // ─── Passkey sign-in (Playbook PR 9) ────────────────────────────────
  // Pre-auth flow — user clicks the button, browser shows their registered
  // passkeys for this rpID, they tap Face ID / Touch ID / Windows Hello,
  // server mints tokens. No password, no SMS, no TOTP. Works regardless of
  // the ENHANCED_AUTH_ENABLED flag because passkeys ARE the authentication
  // — they don't replace anything; they sit alongside the existing flows.
  async function handlePasskeySignIn() {
    setError('');
    setLoading(true);
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const optionsResponse = await authActions.startPasskeySignIn(email || undefined);
      const { session, ...optionsJSON } = optionsResponse;
      // The server hands us a complete PublicKeyCredentialRequestOptionsJSON
      // alongside our session UUID — typed as Record<string, unknown> in the
      // store action because we don't carry the SimpleWebAuthn type into
      // generic store code. Cast through unknown for the lib's strict type.
      const response = await startAuthentication({
        optionsJSON: optionsJSON as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      await authActions.finishPasskeySignIn({ session, response });
      loadCompanyData();
      navigate({ to: '/' });
    } catch (err) {
      const e = err as Error;
      // User dismissed the browser prompt → silently no-op.
      if (e.name === 'NotAllowedError' || /cancel/i.test(e.message)) {
        return;
      }
      setError(e.message || 'Passkey sign-in failed');
    } finally {
      setLoading(false);
    }
  }

  // Feature-detect once; re-render if it changes (it won't, but the hook
  // pattern keeps things tidy).
  const supportsPasskeys = typeof window !== 'undefined' && !!window.PublicKeyCredential;

  // Per-stage right-panel title — replaces the centred "Sign In" header
  // from the legacy single-column layout.
  const stageTitle =
    stage.kind === 'credentials' ? 'Sign in to your workspace' :
    stage.kind === 'otp'         ? 'Verify your identity' :
    stage.kind === 'select-tenant' ? 'Choose a workspace' :
    stage.kind === 'certain-pending' ? 'Sending code…' :
    'Verify your identity'; // certain-otp

  // ─── Render ────────────────────────────────────────────────────────────
  // Split-screen suite login layout — left half is the ARM branded blue
  // gradient with tagline + description; right half hosts the stage UI.
  // Mobile falls back to single-column below `lg`. Pattern shared across
  // the BeCertain suite — see Cross app activity/LOGIN_AND_CHROME_SPEC.md.
  return (
    <div className="min-h-screen flex">
      {/* Left panel — ARM blue accent, hidden on mobile */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          ARM
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl font-bold leading-tight">
            Track every asset.<br />Prove your risk.
          </h1>
          <p className="text-blue-50 text-lg max-w-md">
            ISO 27001 asset and risk management for security teams that need to show their work. Inventory, control mapping, and audit-ready evidence in one place.
          </p>
          <div className="flex items-center gap-2 text-blue-50 text-sm">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
            </svg>
            Secure, role-based access for your whole organisation.
          </div>
        </div>
        <div className="text-xs text-blue-100">© {new Date().getFullYear()} ARM · Certain®</div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile-only branding */}
          <div className="lg:hidden flex items-center gap-2 mb-8 text-blue-700 font-semibold text-lg">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            ARM
          </div>
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{stageTitle}</h1>
            <p className="text-sm text-gray-500 mt-1">Certain® — Asset Risk Manager</p>
          </div>

          {idleNotice && (
            <div className="mb-4 bg-amber-50 text-amber-800 border border-amber-200 px-4 py-3 rounded-lg text-sm">
              You were signed out due to inactivity. Please sign in again.
            </div>
          )}

        {config === null ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="h-32 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            </div>
          </div>
        ) : !config.enhancedAuthEnabled ? (
          // ─── Legacy single-step ─────────────────────────────────────
          <CredentialsCard
            error={error}
            ssoBlock={
              <SsoBlock
                supportsPasskeys={supportsPasskeys}
                becertainEnabled={config.becertainEnabled}
                onCertain={handleCertainStart}
                onPasskey={handlePasskeySignIn}
                disabled={loading}
              />
            }
            form={
              <form onSubmit={handleLegacySubmit} className="space-y-5">
                <CredentialsFields email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
                <SubmitButton loading={loading} label={loading ? 'Signing in…' : 'Sign In'} />
              </form>
            }
            demoBlock={
              config.demoEnabled ? (
                <DemoBlock
                  onAdmin={() => handleDemoSignin('admin')}
                  onMember={() => handleDemoSignin('member')}
                  disabled={loading}
                />
              ) : null
            }
          />
        ) : stage.kind === 'credentials' ? (
          // ─── New flow, step 1 ───────────────────────────────────────
          <CredentialsCard
            error={error}
            ssoBlock={
              <SsoBlock
                supportsPasskeys={supportsPasskeys}
                becertainEnabled={config.becertainEnabled}
                onCertain={handleCertainStart}
                onPasskey={handlePasskeySignIn}
                disabled={loading}
              />
            }
            form={
              <form onSubmit={handleStart} className="space-y-5">
                <CredentialsFields email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
                <SubmitButton loading={loading} label={loading ? 'Sending code…' : 'Send verification code'} />
              </form>
            }
            demoBlock={
              config.demoEnabled ? (
                <DemoBlock
                  onAdmin={() => handleDemoSignin('admin')}
                  onMember={() => handleDemoSignin('member')}
                  disabled={loading}
                />
              ) : null
            }
          />
        ) : stage.kind === 'certain-pending' ? (
          // ─── Certain SSO, waiting for BeCertain to ship the SMS ─────
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-5 text-center">
            <div className="w-10 h-10 mx-auto border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-600">
              Asking Certain to send a verification code to your registered phone…
            </p>
            <button
              type="button"
              onClick={reset}
              className="block w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        ) : stage.kind === 'certain-otp' ? (
          // ─── Certain SSO, OTP step ──────────────────────────────────
          <form onSubmit={handleCertainVerify} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-5">
            <p className="text-sm text-gray-600">
              Certain has sent a verification code to the phone number registered on your Certain account.
              Enter it below to finish signing in.
            </p>
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}
            <div>
              <label htmlFor="certain-otp" className="block text-sm font-medium text-gray-700 mb-1">
                Verification code
              </label>
              <input
                id="certain-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{4,8}"
                maxLength={8}
                required
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-mono text-center tracking-widest text-lg"
                placeholder="123456"
              />
            </div>
            <SubmitButton loading={loading} label={loading ? 'Verifying…' : 'Verify and sign in'} />
            <button
              type="button"
              onClick={reset}
              className="block w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Use a different email
            </button>
          </form>
        ) : stage.kind === 'otp' ? (
          // ─── New flow, step 2 ───────────────────────────────────────
          <form onSubmit={handleVerify} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-5">
            <p className="text-sm text-gray-600">
              {stage.method === 'totp' ? (
                <>Enter the 6-digit code from your authenticator app — or one of your <span className="font-mono">XXXX-XXXX</span> recovery codes if you've lost access.</>
              ) : (
                <>We sent a 6-digit code by SMS to <span className="font-mono">{stage.maskedPhone ?? 'your phone'}</span>. Enter it below to finish signing in.</>
              )}
            </p>
            {stage.mustChangePassword && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                Your administrator has flagged this account to set a new password — you'll be redirected to do that after entering the code.
              </div>
            )}
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                {stage.method === 'totp' ? 'Authenticator code or recovery code' : 'Verification code'}
              </label>
              <input
                id="otp"
                type="text"
                /*
                 * For TOTP we accept either 6 digits OR an XXXX-XXXX recovery
                 * code, so the input is permissive — server validates the
                 * shape that fits the user's enrolment state.
                 */
                inputMode={stage.method === 'totp' ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                maxLength={stage.method === 'totp' ? 9 : 6}
                required
                value={otp}
                onChange={(e) => {
                  const v = stage.method === 'totp'
                    ? e.target.value.toUpperCase().replace(/[^0-9A-Z-]/g, '')
                    : e.target.value.replace(/\D/g, '');
                  setOtp(v);
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-mono text-center tracking-widest text-lg"
                placeholder={stage.method === 'totp' ? '123456 or XXXX-XXXX' : '123456'}
              />
            </div>
            <SubmitButton loading={loading} label={loading ? 'Verifying…' : 'Verify and sign in'} />
            <button
              type="button"
              onClick={reset}
              className="block w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Use a different email
            </button>
          </form>
        ) : (
          // ─── New flow, step 3 (tenant picker) ───────────────────────
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-4">
            <p className="text-sm text-gray-600">
              You belong to multiple workspaces. Choose the one to sign in to:
            </p>
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}
            <ul className="space-y-2">
              {stage.companies.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleSelectTenant(c.id)}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{c.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 capitalize">
                      {c.role.replace('_', ' ')} · {c.permission}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={reset}
              disabled={loading}
              className="block w-full text-center text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Use a different email
            </button>
          </div>
        )}
        </div>{/* max-w-md */}
      </div>{/* right panel */}
    </div>
  );
}

// ─── Small reusable bits to keep the main component readable ─────────────

function CredentialsFields(props: {
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
}) {
  return (
    <>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          value={props.email}
          onChange={(e) => props.setEmail(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
          placeholder="you@example.com"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <Link to="/forgot-password" className="text-sm text-purple-600 hover:text-purple-700">
            Forgot password?
          </Link>
        </div>
        <PasswordField
          id="password"
          required
          autoComplete="current-password"
          value={props.password}
          onChange={(e) => props.setPassword(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
          placeholder="Enter your password"
        />
      </div>
    </>
  );
}

function SubmitButton(props: { loading: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={props.loading}
      className="w-full py-2.5 px-4 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
      style={{ background: 'var(--color-accent)' }}
    >
      {props.label}
    </button>
  );
}

/**
 * "Sign in with passkey" button that sits below the password form. Always
 * enabled (independent of the email field — the discoverable flow asks the
 * browser to list every passkey for this rpID), and when the email IS
 * filled in we pass it through to scope the prompt to that user's keys.
 *
 * Wrapped in a small `or` divider so it's clearly an alternative path
 * rather than a step.
 */
/**
 * Credentials-step shell. Lays out the page consistently for both the legacy
 * one-step flow and the new `credentials` stage of the SMS-OTP flow:
 *
 *   ┌──────────────────────────────────────┐
 *   │  Error banner (if any)               │
 *   │                                      │
 *   │  SSO buttons (Certain / passkey)     │  ← primary when shown
 *   │                                      │
 *   │  ─────── or ───────                  │  ← divider only when SSO exists
 *   │                                      │
 *   │  Email + Password form               │  ← always present, secondary
 *   │  [Sign in / Send verification code]  │
 *   │                                      │
 *   │  Don't have an account? Register…    │
 *   └──────────────────────────────────────┘
 *
 * The SSO block lives OUTSIDE the form so pressing Enter in the email /
 * password fields submits the form (native flow), not the SSO buttons —
 * Enter doing nothing or accidentally firing SSO would both be confusing.
 */
function CredentialsCard({
  error,
  ssoBlock,
  form,
  demoBlock,
}: {
  error: string;
  ssoBlock: React.ReactNode;
  form: React.ReactNode;
  demoBlock?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-5">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}
      {ssoBlock}
      {form}
      {demoBlock}
      <RegisterLink />
    </div>
  );
}

/**
 * Two side-by-side buttons that sign a visitor into the shared North Wind
 * demo tenant password-less. The caller only renders this block when the
 * server reports `demoEnabled: true` on /auth/config, so no feature-flag
 * check lives inside the component itself.
 */
function DemoBlock({
  onAdmin,
  onMember,
  disabled,
}: {
  onAdmin: () => void;
  onMember: () => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-3 pt-1">
      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-xs uppercase tracking-wider text-gray-500">
            see the demo
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onAdmin}
          disabled={disabled}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          View as Admin
        </button>
        <button
          type="button"
          onClick={onMember}
          disabled={disabled}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          View as Member
        </button>
      </div>
      <p className="text-center text-xs text-gray-500">
        Explore North Wind Industrials — a fictional company. The demo tenant resets
        every Sunday.
      </p>
    </div>
  );
}

/**
 * Renders nothing if neither SSO method is enabled. Otherwise renders the
 * SSO buttons as a primary-emphasis block above an "or" divider, signalling
 * "this is the recommended way in" to users for whom SSO is configured.
 */
function SsoBlock({
  supportsPasskeys,
  becertainEnabled,
  onCertain,
  onPasskey,
  disabled,
}: {
  supportsPasskeys: boolean;
  becertainEnabled: boolean;
  onCertain: () => void;
  onPasskey: () => void;
  disabled: boolean;
}) {
  if (!supportsPasskeys && !becertainEnabled) return null;
  return (
    <>
      <div className="space-y-2">
        {becertainEnabled && (
          <CertainSignIn onClick={onCertain} disabled={disabled} primary />
        )}
        {supportsPasskeys && (
          <PasskeySignIn onClick={onPasskey} disabled={disabled} hideDivider />
        )}
      </div>
      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-2 text-xs uppercase tracking-wide text-gray-400">
            or use email and password
          </span>
        </div>
      </div>
    </>
  );
}

function PasskeySignIn({
  onClick,
  disabled,
  hideDivider,
}: {
  onClick: () => void;
  disabled: boolean;
  hideDivider?: boolean;
}) {
  return (
    <>
      {!hideDivider && (
        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-xs uppercase tracking-wide text-gray-400">or</span>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
        </svg>
        Sign in with a passkey
      </button>
    </>
  );
}

/**
 * "Sign in with Certain®" button — triggers the BeCertain-proxied SSO flow.
 * Reuses the email + password the user has already typed; the parent does
 * the actual /becertain/login + polling + OTP step.
 *
 * `primary` flips it from the outlined-grey "alternate" look to the brand
 * purple "primary" look — used at the top of the credentials stage when
 * Certain SSO is enabled and we want users to default to it.
 */
function CertainSignIn({
  onClick,
  disabled,
  primary,
}: {
  onClick: () => void;
  disabled: boolean;
  primary?: boolean;
}) {
  if (primary) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium text-white shadow-sm hover:opacity-95 disabled:opacity-50 transition-opacity"
        style={{ background: 'var(--color-accent)' }}
      >
        <img src="/certain-logo.png" alt="" aria-hidden="true" className="h-4 object-contain bg-white rounded px-1 py-0.5" />
        Sign in with Certain®
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    >
      <img src="/certain-logo.png" alt="" aria-hidden="true" className="h-4 object-contain" />
      Sign in with Certain®
    </button>
  );
}

function RegisterLink() {
  return (
    <p className="text-center text-sm text-gray-500">
      Don't have an account?{' '}
      <Link to="/register" className="text-purple-600 hover:text-purple-700 font-medium">
        Register your company
      </Link>
    </p>
  );
}
